import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, StyleSheet, ActivityIndicator } from 'react-native';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/context/ThemeContext';
import { useAuthStore } from '@/store';
import { useTranslation } from '@/hooks/useTranslation';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import { resolvePaymentRetrySettingsPage } from '@/constants/utils/webCheckoutRouting';
import { trackWhopPurchase } from '@/constants/utils/analytics';
import { trackPurchaseCompleted, trackPurchaseFailed } from '@/constants/utils/analyticsEvents';

const REFRESH_INTERVAL_MS = 2000;
const REFRESH_ATTEMPTS = 5;

const createStyles = (colors) => StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.solid_color_primary,
    paddingHorizontal: 24,
  },
  card: {
    width: '100%',
    maxWidth: 440,
    alignItems: 'center',
    gap: 16,
    paddingVertical: 40,
    paddingHorizontal: 28,
    borderRadius: 16,
    backgroundColor: colors.solid_color_secondary,
    borderWidth: 1,
    borderColor: colors.border_color_secondary,
  },
  iconWrap: {
    width: 64,
    height: 64,
    borderRadius: 32,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: colors.solid_color_tertiary || colors.solid_color_secondary,
    marginBottom: 4,
  },
  actions: {
    width: '100%',
    gap: 10,
    marginTop: 8,
  },
});

export default function PaymentSuccessfulPage() {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => createStyles(colors), [colors]);
  const router = useRouter();
  const fetchUser = useAuthStore((state) => state.fetchUser);
  const fetchUserCredits = useAuthStore((state) => state.fetchUserCredits);
  // Processors that bounce the buyer to their bank for 3D Secure return here
  // with ?status=error when the payment failed or was canceled. Nothing was
  // purchased in that case, so this page must not claim success.
  const { status, checkoutType } = useLocalSearchParams();
  const failed = status === 'error';
  const retrySettingsPage = resolvePaymentRetrySettingsPage(checkoutType);
  const [refreshing, setRefreshing] = useState(!failed);
  const attemptsRef = useRef(0);

  // A buyer sent to their bank for 3D Secure leaves the page mid-checkout, so
  // this redirect is the only place their outcome is observable client-side.
  // `trackPurchaseCompleted` dedupes against the in-page completion callback for
  // the buyers who never left.
  useEffect(() => {
    const checkoutTypeName = typeof checkoutType === 'string' ? checkoutType : undefined;
    // Swapping a card returns through this same page and buys nothing.
    if (checkoutTypeName === 'update-payment') return;
    if (failed) {
      trackPurchaseFailed({ checkoutType: checkoutTypeName, reason: 'processor_redirect' });
      return;
    }
    trackPurchaseCompleted({ checkoutType: checkoutTypeName });
  }, [failed, checkoutType]);

  useEffect(() => {
    if (failed) return;
    trackWhopPurchase({ id: `payment-successful:${checkoutType || 'unknown'}` });
  }, [failed, checkoutType]);

  useEffect(() => {
    if (failed) return undefined;

    let cancelled = false;
    let timer = null;

    const refresh = async () => {
      try {
        const tasks = [];
        if (fetchUser) tasks.push(fetchUser());
        if (fetchUserCredits) tasks.push(fetchUserCredits({ force: true }));
        await Promise.all(tasks);
      } catch (err) {
        console.warn('[payment-successful] Failed to refresh account after purchase:', err);
      }

      attemptsRef.current += 1;
      if (cancelled) return;

      // Webhooks can land a beat after the processor redirects. Keep polling
      // briefly so the account state catches up before the user leaves.
      if (attemptsRef.current < REFRESH_ATTEMPTS) {
        timer = setTimeout(refresh, REFRESH_INTERVAL_MS);
        return;
      }
      setRefreshing(false);
    };

    refresh();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [failed, fetchUser, fetchUserCredits]);

  return (
    <View style={styles.container}>
      <MetaHead
        title={failed ? t('pages.paymentSuccessful.errorMetaTitle') : t('pages.paymentSuccessful.metaTitle')}
        description={t('pages.paymentSuccessful.metaDescription')}
        noindex
      />
      <View style={styles.card}>
        <View style={styles.iconWrap}>
          {failed ? (
            <Icon name="circle-exclamation" size={32} color={colors.text_color_primary} solid />
          ) : refreshing ? (
            <ActivityIndicator size="small" color={colors.text_color_primary} />
          ) : (
            <Icon name="circle-check" size={32} color={colors.text_color_primary} solid />
          )}
        </View>
        <Text size="xl" weight="bold" style={{ textAlign: 'center' }}>
          {failed ? t('pages.paymentSuccessful.errorTitle') : t('pages.paymentSuccessful.title')}
        </Text>
        <Text size="m" color="secondary" style={{ textAlign: 'center' }}>
          {failed ? t('pages.paymentSuccessful.errorSubtitle') : t('pages.paymentSuccessful.subtitle')}
        </Text>
        <View style={styles.actions}>
          {failed ? (
            <>
              <Button
                variant="primary"
                text={t('pages.paymentSuccessful.errorRetry')}
                onPress={() => router.replace(`/settings?page=${retrySettingsPage}`)}
                isFullWidth
              />
              <Button
                variant="secondary"
                text={t('pages.paymentSuccessful.continue')}
                onPress={() => router.replace('/app')}
                isFullWidth
              />
              <Text size="s" color="secondary" style={{ textAlign: 'center' }}>
                {t('pages.paymentSuccessful.errorContact')}
              </Text>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                text={t('pages.paymentSuccessful.continue')}
                onPress={() => router.replace('/app')}
                isFullWidth
              />
              <Button
                variant="secondary"
                text={t('pages.paymentSuccessful.viewCredits')}
                onPress={() => router.replace('/settings?page=credits')}
                isFullWidth
              />
            </>
          )}
        </View>
      </View>
    </View>
  );
}
